import React, { useState } from 'react';
import { isRealHuman, isExternalAgent, isMaintainer, agentModelName } from '../../utils/playerKind';
import { hueOf } from '../../utils/exploreStart';

// 👥 Who's here (the People panel + the top bar's faces). From the PAGE's
// users (its activeUsers: the seats present right now). An AI wears the cyan
// ring (brand art: "every AI shares one cyan glow"), decided by
// utils/playerKind.isRealHuman, NEVER by is_human: a connected agent sits
// is_human:true on purpose. Display only: nothing here gates behavior.

export const seatKey = (u, i = 0) => String((u && (u.game_player_id || u.id)) || `u${i}`);
export const isYouSeat = (u, viewerSeatId) => !!(viewerSeatId && u && (u.game_player_id === viewerSeatId || u.id === viewerSeatId));
export const isAiSeat = (u) => !isRealHuman(u);
const imgOf = (u) => (u && (u.profile_image_url || u.data?.profile_image_url)) || null;

/** The line under a name: an AI's model, a visiting agent's marker, the admin seat's tag. */
export function seatLine(u) {
  if (!u) return '';
  if (isExternalAgent(u)) return agentModelName(u) || 'Visiting AI';
  if (isRealHuman(u)) return isMaintainer(u) ? 'System maintenance' : '';
  const m = typeof u.model === 'string' && u.model && u.model !== 'Human Player' && u.model !== 'AI' ? u.model : '';
  return m ? (m.split('/')[1] || m) : 'AI';
}

/** AIs first, then people (you first among them): the faces' and the list's order. */
export function orderSeats(users, viewerSeatId) {
  const list = (Array.isArray(users) ? users : []).filter(Boolean);
  const ais = list.filter(isAiSeat);
  const ppl = list.filter((u) => !isAiSeat(u));
  const you = ppl.filter((u) => isYouSeat(u, viewerSeatId));
  return { ais, people: [...you, ...ppl.filter((u) => !isYouSeat(u, viewerSeatId))] };
}

export function Face({ user, size = 26 }) {
  const name = String(user?.name || '?');
  const src = imgOf(user);
  const [broken, setBroken] = useState(null); // the src that failed: a new picture tries again
  const img = src && broken !== src ? src : null;
  const letter = name.replace(/^(The |A )/i, '').trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      className="rs-av"
      data-ai={isAiSeat(user) ? '1' : '0'}
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.44), '--rs-h': hueOf(name) }}
    >
      {img ? <img src={img} alt="" loading="lazy" onError={() => setBroken(img)} /> : letter}
    </span>
  );
}

export default function RoomPeople({ users = [], viewerSeatId = null }) {
  const { ais, people } = orderSeats(users, viewerSeatId);
  if (!ais.length && !people.length) return <div className="rs-none">Nobody here yet.</div>;
  const row = (u, i) => {
    const you = isYouSeat(u, viewerSeatId);
    const line = seatLine(u);
    return (
      <div className="rs-pl" key={seatKey(u, i)} data-rs-seat={seatKey(u, i)} data-ai={isAiSeat(u) ? '1' : '0'}>
        <Face user={u} size={30} />
        <div className="rs-plt">
          <b>{u.name || 'Someone'}{you ? <span className="rs-you"> (you)</span> : null}</b>
          {line ? <span>{line}</span> : null}
        </div>
      </div>
    );
  };
  return (
    <div data-rs-people="">
      {people.length ? <div className="rs-pgrp">People · {people.length}</div> : null}
      {people.map(row)}
      {ais.length ? <div className="rs-pgrp">AIs · {ais.length}</div> : null}
      {ais.map(row)}
    </div>
  );
}
